import React from "react";

/** Classify a Task based on logged minutes + subtask count (matches backend logic). */
export function calcTaskSize(task: any): "SMALL" | "MEDIUM" | "LARGE" {
  const hours = (task?.totalLoggedMinutes ?? 0) / 60;
  const subs  = (task?.subTasks?.length ?? task?._count?.subTasks ?? 0);
  if (hours > 40 || subs >= 9) return "LARGE";
  if (hours <= 8 && subs <= 2) return "SMALL";
  return "MEDIUM";
}

/** Classify a Ticket based on time spent (minutes). */
export function calcTicketSize(ticket: any): "SMALL" | "MEDIUM" | "LARGE" {
  const mins = ticket?.timeSpent ?? 0;
  if (mins > 240) return "LARGE";
  if (mins > 30)  return "MEDIUM";
  return "SMALL";
}

const SIZE_META: Record<string, { label: string; short: string; badge: string; dot: string }> = {
  SMALL:  { label: "Small",  short: "S", badge: "bg-green-100 text-green-700",   dot: "🟢" },
  MEDIUM: { label: "Medium", short: "M", badge: "bg-yellow-100 text-yellow-700", dot: "🟡" },
  LARGE:  { label: "Large",  short: "L", badge: "bg-red-100 text-red-700",       dot: "🔴" },
};

interface SizeBadgeProps {
  size: "SMALL" | "MEDIUM" | "LARGE";
  compact?: boolean;
  className?: string;
}

/** Visual badge for size — used across Tasks & Tickets. */
export function SizeBadge({ size, compact, className = "" }: SizeBadgeProps) {
  const meta = SIZE_META[size] || SIZE_META.SMALL;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-semibold ${meta.badge} ${
        compact ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"
      } ${className}`}
      title={meta.label}
    >
      <span>{meta.dot}</span>
      <span>{compact ? meta.short : meta.label}</span>
    </span>
  );
}

/** Short inline badge (icon + letter only) for tight layouts. */
export function SizeBadgeCompact({ size, className = "" }: { size: any; className?: string }) {
  const meta = SIZE_META[size] || SIZE_META.SMALL;
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-bold text-[10px] px-1.5 py-0.5 ${meta.badge} ${className}`}
      title={meta.label}
    >
      {meta.short}
    </span>
  );
}
